import Model, { attr, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { isNone } from '@ember/utils';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export enum AccessLevel {
  full = 'full',
  readOnly = 'read_only',
}

export default class AirbnbOfficialConnectionModel extends Model {
  @attr('number') airbnbUserId!: number;
  @attr('string') fullName!: string;
  @attr('string') pictureUrl!: string;
  @attr('string') email!: string;
  @attr('string') code!: string;
  @attr('string') status!: string;
  @attr('string') accessLevel!: AccessLevel;
  @attr('number') propertiesCount!: number;
  @attr('number') processedPropertiesCount!: number | null;
  @attr('boolean') apiAccess!: boolean;
  @attr('array') selectedListingIds!: string[];
  @attr('boolean') defaultNewListingPromotionEnabled!: boolean;

  @hasMany('airbnb-official-link', {
    inverse: 'airbnbOfficialConnection',
    async: false,
  })
  airbnbOfficialLinks!: ModelRegistry['airbnb-official-link'][];

  @hasMany('property', { inverse: null, async: false })
  properties!: ModelRegistry['property'][];

  @hasMany('airbnb-official-connection-listing', {
    inverse: null,
    async: false,
  })
  allListings!: ModelRegistry['airbnb-official-connection-listing'][];

  @cached
  get isComplete(): boolean {
    return this.status === 'complete';
  }

  @cached
  get isConnected(): boolean {
    return this.status === 'connected';
  }

  @cached
  get readOnly(): boolean {
    return this.accessLevel === AccessLevel.readOnly;
  }

  @cached
  get isFullyConnected(): boolean {
    return this.accessLevel === AccessLevel.full && this.isComplete;
  }

  @cached
  get propertiesCountPresent(): boolean {
    return !isNone(this.propertiesCount) && this.propertiesCount !== 0;
  }

  @cached
  get channelConnectionId(): string {
    return `airbnb_${this.id}`;
  }

  @cached
  get isOnboarding(): boolean {
    if (!this.isConnected) {
      return false;
    }

    return (
      this.processedPropertiesCount === 0 ||
      this.processedPropertiesCount === null ||
      this.processedPropertiesCount < this.propertiesCount
    );
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'airbnb-official-connection': AirbnbOfficialConnectionModel;
  }
}
